import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import { getSupplierNoticeForEdit, updateSupplierNotice } from '../../../models/noticesModel';
import { getAreas } from '../../../models/areasModel';
import { toast } from 'react-toastify';
import WysiwygEditor from '../../../components/common/WysiwygEditor';

function NoticeEdit() {
  const { noticeId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [areas, setAreas] = useState([]);
  
  const [formData, setFormData] = useState({
    notice_title: '',
    notice_description: '',
    notice_text: '',
    notice_type: 'announcement',
    selected_areas: []
  });

  useEffect(() => {
    if (user && noticeId) {
      fetchNotice();
      fetchAreas();
    }
  }, [user, noticeId]);

  const fetchNotice = async () => {
    try {
      setLoading(true);
      const response = await getSupplierNoticeForEdit(noticeId);
      
      if (response.status === 1) {
        const notice = response.data?.row || response.notice;
        setFormData({
          notice_title: notice.notice_title || '',
          notice_description: notice.notice_description || '',
          notice_text: notice.notice_text || '',
          notice_type: notice.notice_type || 'announcement',
          selected_areas: notice.selected_areas || notice.areas || []
        });
      } else {
        throw new Error(response.message || 'Failed to fetch notice');
      }
    } catch (err) {
      console.error('Error fetching notice:', err);
      toast.error(err.message || 'Failed to load notice');
      navigate('..');
    } finally {
      setLoading(false);
    }
  };

  const fetchAreas = async () => {
    try {
      console.log('Fetching areas...');
      const response = await getAreas();
      console.log('Areas response:', response);
      if (response.status === 1) {
        setAreas(response.areas || []);
        console.log('Areas set:', response.areas);
      } else {
        console.warn('Failed to fetch areas:', response.message);
      }
    } catch (error) {
      console.error('Error fetching areas:', error);
      toast.error('Failed to load areas');
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleAreaChange = (areaId) => {
    setFormData(prev => ({
      ...prev,
      selected_areas: prev.selected_areas.includes(areaId)
        ? prev.selected_areas.filter(id => id !== areaId)
        : [...prev.selected_areas, areaId]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.notice_title.trim()) {
      toast.error('Notice title is required');
      return;
    }

    if (!formData.notice_text.trim()) {
      toast.error('Notice content is required');
      return;
    }

    try {
      setSaving(true);
      const response = await updateSupplierNotice(noticeId, formData);
      
      if (response.status === 1) {
        toast.success('Notice updated successfully');
        navigate('..', { state: { fromForm: true } });
      } else {
        throw new Error(response.message || 'Failed to update notice');
      }
    } catch (err) {
      console.error('Error updating notice:', err);
      toast.error(err.message || 'Failed to update notice');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    navigate(`../${noticeId}`);
  };

  const handleBack = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-[#0087D4]"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-6">
      {/* Header with Back Navigation */}
      <div className="mb-6">
        <button
          onClick={handleBack}
          className="flex items-center text-[#0087D4] hover:text-blue-600 transition-colors mb-4"
        >
          <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Back
        </button>
        <h1 className="text-2xl font-semibold text-gray-900">Edit Notice</h1>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column: Notice Details */}
          <div className="lg:col-span-1 space-y-6">
            <div className="bg-white p-6 rounded-lg shadow border">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Notice Details</h3>
              <div className="space-y-4">
                <div>
                  <label htmlFor="notice_type" className="block text-sm font-medium text-gray-700">
                    Notice Type
                  </label>
                  <select
                    id="notice_type"
                    name="notice_type"
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-[#0087D4] focus:border-[#0087D4] sm:text-sm"
                    value={formData.notice_type}
                    onChange={handleInputChange}
                  >
                    <option value="announcement">Announcement</option>
                    <option value="maintenance">Maintenance</option>
                    <option value="urgent">Urgent</option>
                    <option value="information">Information</option>
                  </select>
                </div>

                {/* Areas Selection */}
                {areas.length > 0 && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Target Areas
                    </label>
                    <div className="max-h-48 overflow-y-auto border border-gray-300 rounded-md p-3">
                      {areas.map(area => (
                        <label key={area.area_id} className="flex items-center space-x-2 py-1">
                          <input
                            type="checkbox"
                            checked={formData.selected_areas.includes(area.area_id)}
                            onChange={() => handleAreaChange(area.area_id)}
                            className="rounded border-gray-300 text-[#0087D4] focus:ring-[#0087D4]"
                          />
                          <span className="text-sm text-gray-700">{area.area_name}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Actions */}
            <div className="bg-white p-6 rounded-lg shadow border">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Actions</h3>
              <div className="flex flex-col gap-3">
                <button 
                  type="submit" 
                  className="inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-[#0087D4] hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#0087D4] w-full"
                  disabled={saving}
                >
                  {saving ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Updating...
                    </>
                  ) : 'Update Notice'}
                </button>
                <button 
                  type="button" 
                  className="inline-flex items-center justify-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#0087D4] w-full"
                  onClick={handleCancel}
                  disabled={saving}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>

          {/* Right Column: Notice Content */}
          <div className="lg:col-span-2">
            <div className="bg-white p-6 rounded-lg shadow border">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Notice Content</h3>
              <div className="space-y-4">
                <div>
                  <label htmlFor="notice_title" className="block text-sm font-medium text-gray-700">
                    Notice Title *
                  </label>
                  <input
                    type="text"
                    id="notice_title"
                    name="notice_title"
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-[#0087D4] focus:border-[#0087D4] sm:text-sm text-gray-900"
                    value={formData.notice_title}
                    onChange={handleInputChange}
                    required
                    placeholder="Enter notice title"
                  />
                </div>

                <div>
                  <label htmlFor="notice_description" className="block text-sm font-medium text-gray-700">
                    Brief Description
                  </label>
                  <input
                    type="text"
                    id="notice_description"
                    name="notice_description"
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-[#0087D4] focus:border-[#0087D4] sm:text-sm text-gray-900"
                    value={formData.notice_description}
                    onChange={handleInputChange}
                    placeholder="Brief summary of the notice"
                  />
                </div>

                <div>
                  <label htmlFor="notice_text" className="block text-sm font-medium text-gray-700 mb-2">
                    Content *
                  </label>
                  <div className="border border-gray-300 rounded-md">
                    <WysiwygEditor
                      value={formData.notice_text}
                      onChange={(content) => setFormData(prev => ({
                        ...prev,
                        notice_text: content
                      }))}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
}

export default NoticeEdit; 